import {
  Directive,
  TemplateRef,
  ViewContainerRef,
  inject,
  Input,
  effect,
  signal,
} from '@angular/core';
import { AuthStore } from './auth.store';

type HasPermissionInput =
  | string
  | string[]
  | {
  all?: string[] | string;
  some?: string[] | string;
};

@Directive({
  selector: '[hasPermission]',
})
export class HasPermissionDirective {
  private tpl = inject(TemplateRef);
  private vcr = inject(ViewContainerRef);
  private authStore = inject(AuthStore);

  /** Store if the block is currently rendered */
  private isShown = false;

  /** Raw input */
  private _input = signal<HasPermissionInput | undefined>(undefined);

  @Input()
  set hasPermission(value: HasPermissionInput) {
    this._input.set(value);
  }

  constructor() {
    // Run whenever the input OR the authStore permissions change
    effect(() => {
      if (!this._input()) return;

      const inputVal = this._input()!;
      const canShow = this.checkPermissions(inputVal);

      if (canShow && !this.isShown) {
        this.vcr.clear();
        this.vcr.createEmbeddedView(this.tpl, { $implicit: true });
        this.isShown = true;
      } else if (!canShow && this.isShown) {
        this.vcr.clear();
        this.isShown = false;
      }
    });
  }

  private normalize(input: HasPermissionInput): {
    all?: string[];
    some?: string[];
  } {
    if (typeof input === 'string') return { all: [input] };
    if (Array.isArray(input)) return { all: input };

    // ensure all/some are arrays
    return {
      all: input.all
        ? Array.isArray(input.all)
          ? input.all
          : [input.all]
        : undefined,
      some: input.some
        ? Array.isArray(input.some)
          ? input.some
          : [input.some]
        : undefined,
    };
  }

  private checkPermissions(input: HasPermissionInput): boolean {
    const normalized = this.normalize(input);

    if (normalized.all) {
      return this.authStore.hasAllPermissions(normalized.all);
    }

    if (normalized.some) {
      return this.authStore.hasSomePermissions(normalized.some);
    }

    return false;
  }
}

